import React, { useId, type ReactNode } from 'react';
import IconButton from '../../atoms/IconButton';
import {
  PANEL_HEADER_DENSITY_REGULAR,
  type PanelHeaderDensity,
} from './PanelHeader.constants';
import {
  Actions,
  Leading,
  Root,
  Subtitle,
  Title,
  TitleBlock,
} from './PanelHeader.styled';

export interface PanelHeaderProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'title' | 'children'> {
  /**
   * Visible title — usually a string, but accepts any node when the title
   * needs an inline status pill, badge, or `InlineRenameField`.
   */
  title: ReactNode;
  /** Optional second line below the title. */
  subtitle?: ReactNode;
  /**
   * Optional leading slot — typically an icon, badge, or back-button.
   * Renders before the title block.
   */
  leading?: ReactNode;
  /**
   * Optional trailing actions — `IconButton`s, `Button`s, or any cluster.
   * Pinned to the right edge with a 4px gap. The auto-injected close
   * button (when `onClose` is set) renders AFTER these actions.
   */
  actions?: ReactNode;
  /**
   * When provided, an `IconButton` close button is auto-rendered as the
   * last item in the trailing slot. Hand-roll your own if you need
   * non-standard placement.
   */
  onClose?: () => void;
  /** Override the close button's aria-label. Defaults to `"Close"`. */
  closeAriaLabel?: string;
  /** Vertical density. Defaults to `regular`. */
  density?: PanelHeaderDensity;
  /**
   * Stick the header to the top of its scroll container. Defaults to
   * `false`. Useful inside a `CollapsibleSidePanel` or any tall panel.
   */
  sticky?: boolean;
  /**
   * Draw a hairline border below the header. Defaults to `true` since the
   * common use case (panel chrome) needs a visual separation from the
   * scrolling body. Set to `false` for headers inside a card.
   */
  bordered?: boolean;
  /**
   * The element semantics. Defaults to `<header>`. Override to `<div>` if
   * the header is nested inside another `<header>` (a screen reader will
   * deduplicate the landmark otherwise).
   */
  as?: 'header' | 'div';
  /**
   * Override the auto-generated id stamped on the title element. Use
   * this when a parent landmark needs to wire `aria-labelledby` to the
   * exact same id from the outside (e.g. `<CollapsibleSidePanel
   * ariaLabelledBy={titleId} header={<PanelHeader titleId={titleId} ... />}
   * />`). When omitted, a stable `useId()` value is generated and the
   * root element references it via `aria-labelledby` automatically, so
   * the header is still self-labelled even without consumer wiring.
   */
  titleId?: string;
  dataTestId?: string;
}

const CloseGlyph = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" width="20" height="20">
    <path
      d="M6 6 18 18 M18 6 6 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * Header chrome shared by every side-panel / card / drawer in the app:
 * leading slot + (title + optional subtitle) + actions slot, with an
 * auto-injected close `IconButton` when `onClose` is set.
 *
 * The header is always self-labelled: a stable id is generated for the
 * title element and stamped on the root via `aria-labelledby`, so the
 * header reads as a properly-named landmark even with no consumer
 * wiring. Pass `titleId` when a parent landmark needs to reference the
 * exact same id from the outside (e.g. a `CollapsibleSidePanel` whose
 * `ariaLabelledBy` must point at this header's title).
 *
 * @example Stand-alone (auto-labelled)
 *   <PanelHeader title="Run details" subtitle="Run #1242" />
 *
 * @example Linked to a parent landmark
 *   const titleId = useId();
 *   <CollapsibleSidePanel open={open} onClose={close} ariaLabelledBy={titleId}>
 *     <PanelHeader
 *       titleId={titleId}
 *       title="Run details"
 *       subtitle="Run #1242"
 *       leading={<IconChip icon={<MdPlayArrow />} />}
 *       actions={<IconButton ariaLabel="More" icon={<MdMoreVert />} />}
 *       onClose={close}
 *       sticky
 *     />
 *     <SidePanelBody>{...}</SidePanelBody>
 *   </CollapsibleSidePanel>
 */
const PanelHeader: React.FC<PanelHeaderProps> = ({
  title,
  subtitle,
  leading,
  actions,
  onClose,
  closeAriaLabel = 'Close',
  density = PANEL_HEADER_DENSITY_REGULAR,
  sticky = false,
  bordered = true,
  as = 'header',
  titleId: titleIdProp,
  dataTestId = 'panel-header',
  ...rest
}) => {
  // Stable fallback id so the header self-labels even when the consumer
  // doesn't pass `titleId`. A consumer-supplied id wins so a parent
  // landmark (CollapsibleSidePanel, ConfirmDialog, etc.) can reference
  // the exact same value from the outside.
  const generatedTitleId = useId();
  const titleId = titleIdProp ?? generatedTitleId;
  // Honour an `aria-labelledby` passed via {...rest}; otherwise wire it
  // to the title id so the header is always self-labelled per the
  // documented contract.
  const restAriaLabelledBy =
    (rest as { 'aria-labelledby'?: string })['aria-labelledby'];
  const ariaLabelledBy = restAriaLabelledBy ?? titleId;

  return (
    <Root
      {...rest}
      as={as}
      $density={density}
      $sticky={sticky}
      $bordered={bordered}
      aria-labelledby={ariaLabelledBy}
      data-testid={dataTestId}
      data-density={density}
    >
      {leading && (
        // No `aria-hidden` here — the leading slot can host interactive
        // controls (back button, dropdown trigger, avatar with menu)
        // and hiding it from the a11y tree would silently strip those
        // from screen readers. Consumers who pass a purely decorative
        // glyph should mark it `aria-hidden` themselves.
        <Leading data-testid={`${dataTestId}__leading`}>{leading}</Leading>
      )}
      <TitleBlock data-testid={`${dataTestId}__title-block`}>
        <Title
          $density={density}
          id={titleId}
          data-testid={`${dataTestId}__title`}
        >
          {title}
        </Title>
        {subtitle && (
          <Subtitle
            $density={density}
            data-testid={`${dataTestId}__subtitle`}
          >
            {subtitle}
          </Subtitle>
        )}
      </TitleBlock>
      {(actions || onClose) && (
        <Actions data-testid={`${dataTestId}__actions`}>
          {actions}
          {onClose && (
            <IconButton
              ariaLabel={closeAriaLabel}
              icon={<CloseGlyph />}
              size="sm"
              onClick={onClose}
              dataTestId={`${dataTestId}__close`}
            />
          )}
        </Actions>
      )}
    </Root>
  );
};

export default PanelHeader;
